import { subscribe } from 'valtio';

import { FileUploadState } from '@cartwhl/ui/feature/FileUploader';

import { DEFAULT_UPLOAD_TIME } from 'stores/characterPickerStore';

/*
  This is a utility function for uploading files to the server.
  It is used to start and stop the processing counter, and to upload a file to the server.
  It also includes a polling system that polls the server for the status of the upload
*/
export default function uploadUtil(
  uploadState: FileUploadState,
  pollFn?: (uploadId: string) => Promise<void>,
) {
  let interval: NodeJS.Timeout | undefined;
  let pollingInterval: NodeJS.Timeout | undefined;
  let hasStartedPolling = false;

  const unsubscribe = subscribe(uploadState, (ops) => {
    ops.forEach((operation) => {
      const [op, path, value, prevValue] = operation;
      if (
        (op === 'set' && path.includes('status') && value === 'PROCESSING') ||
        ((value as any) || { status: '' }).status === 'PROCESSING'
      ) {
        startProcessingCounter();
      }
    });
  });

  /**
   * Starts the processing counter - optionally includes an override to check on the progress of a job
   * after it hits 100%
   * @param pollFnOverride - Optional function to override the default polling function
   * @param pollIntervalMs - Optional interval for polling (default: 15 seconds)
   */
  const startProcessingCounter = (
    pollFnOverride?: (uploadId: string) => Promise<void>,
    pollIntervalMs = 15000,
  ) => {
    clearInterval(interval);
    clearInterval(pollingInterval);
    hasStartedPolling = false;

    const estimatedProcessingTime =
      uploadState.estimatedProcessingTime || DEFAULT_UPLOAD_TIME; // default: 40 seconds
    interval = setInterval(() => {
      if (uploadState.status === 'SUCCESS' || uploadState.status === 'ERROR') {
        clearInterval(interval);
        clearInterval(pollingInterval);
        unsubscribe();
        return;
      }
      if (uploadState.createdAt) {
        const elapsedTime =
          Date.now() - new Date(uploadState.createdAt).getTime();
        const progress = Math.min(
          Math.floor((elapsedTime / estimatedProcessingTime) * 100),
          99,
        );
        uploadState.processingPercentage = Math.round(progress);

        const pollingFunction = pollFnOverride || pollFn;
        if (
          progress >= 99 &&
          !hasStartedPolling &&
          pollingFunction &&
          uploadState.uploadID
        ) {
          hasStartedPolling = true;
          clearInterval(pollingInterval); // Clear any existing polling interval

          // Start polling immediately and then every pollIntervalMs
          const uploadId = uploadState.uploadID;
          if (uploadId) {
            pollingFunction(uploadId).catch(console.error);

            pollingInterval = setInterval(() => {
              if (
                uploadState.status === 'SUCCESS' ||
                uploadState.status === 'ERROR'
              ) {
                console.log('Stopping polling');
                clearInterval(pollingInterval);
                return;
              }
              console.log(
                'Upload progress has stalled, manually polling for results',
              );
              pollingFunction(uploadId).catch(console.error);
            }, pollIntervalMs);
          }
        }
      }
    }, 500);
  };

  const stopProcessingCounter = () => {
    clearInterval(interval);
    clearInterval(pollingInterval);
    hasStartedPolling = false;
  };

  const upload = async (uploadURL: string, file: File) => {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          if (uploadState) uploadState.fileTransferPercent = percentComplete;
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else if (xhr.status === 500) {
          reject('Server error. Please try again later.');
        } else {
          reject('An unexpected error occurred while uploading.');
        }
      });

      xhr.addEventListener('error', () => {
        reject('Network error. Check your internet connection.');
      });

      xhr.open('PUT', uploadURL);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    }).catch((reason: string) => {
      if (uploadState) {
        uploadState.status = 'ERROR';
        uploadState.errorReasons = [reason];
      }
    });
  };

  return { upload, startProcessingCounter, stopProcessingCounter };
}
